import { createSlice } from '@reduxjs/toolkit';
import { z } from 'zod';
import { DateTime } from 'luxon';

import * as profileActions from 'app-components/flux/credit-profile/actions';
import * as reportActions from 'app-components/flux/credit-report/actions';
import type { CreditReportType } from 'app-components/flux/credit-report/types';

const summarySchema = z.object({
  registrationId: z.string().min(1),
  creditScore: z
    .union([z.number(), z.string().min(1)])
    .refine((value) => Number.isFinite(Number(value)) && Number(value) > 0),
  refreshDate: z.string().refine((value) => DateTime.fromISO(value).isValid),
});
export type CachedCreditScore = z.infer<typeof summarySchema>;
export type CreditScoreCacheState = {
  summary: CachedCreditScore | null;
  // Confirmed by the current session's profile response; never restored from cache.
  registrationId: string | null;
};
const initialState: CreditScoreCacheState = { summary: null, registrationId: null };

function saveReport(state: CreditScoreCacheState, report: CreditReportType | undefined): void {
  const parsed = summarySchema.safeParse({
    registrationId: state.registrationId,
    creditScore: report?.creditScore,
    refreshDate: report?.refreshDate,
  });
  state.summary = parsed.success ? parsed.data : null;
}

// Ignore a response for a registration that was replaced while the request was in flight.
const isForCurrentRegistration = (state: CreditScoreCacheState, requestedId: unknown): boolean =>
  Boolean(state.registrationId) && (!requestedId || requestedId === state.registrationId);

export const creditScoreCacheSlice = createSlice({
  name: 'creditScoreCache',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(profileActions.getCreditProfileSuccess, (state, { payload }) => {
      const profiles = payload.resources;
      const profile = profiles.find((item) => item.status === 'CREDIT_REPORT_RECEIVED') ?? profiles[0];
      state.registrationId = profile?.id ?? profile?.creditProfileRegistrationId ?? null;
      if (state.summary?.registrationId !== state.registrationId || profile?.status !== 'CREDIT_REPORT_RECEIVED') {
        state.summary = null;
      }
    });
    builder.addCase(profileActions.createCreditProfileSuccess, (state, { payload }) => {
      state.registrationId = payload.id ?? payload.creditProfileRegistrationId ?? null;
      state.summary = null;
    });
    builder.addCase(profileActions.deleteCreditProfileSuccess, () => initialState);
    builder.addCase(reportActions.clearOrphanedCreditReport, (state) => {
      state.summary = null;
    });
    builder.addCase(reportActions.getCreditReportSuccess, (state, action) => {
      const resourceUrl = action.meta?.config?.resourceBaseUrl;
      const query = typeof resourceUrl === 'string' ? resourceUrl.split('?')[1] : undefined;
      const requestedId = query ? new URLSearchParams(query).get('creditProfileRegistrationId') : null;
      if (!isForCurrentRegistration(state, requestedId)) return;
      saveReport(state, action.payload.resources[0]);
    });
    // The refresh sagas name the requested registration in meta.props.
    builder.addCase(reportActions.refreshCreditReportSuccess, (state, { payload, meta }) => {
      if (!isForCurrentRegistration(state, meta?.props?.creditProfileRegistrationId)) return;
      saveReport(state, payload);
    });
    builder.addCase(reportActions.getRefreshedCreditReportSuccess, (state, { payload, meta }) => {
      if (!isForCurrentRegistration(state, meta?.props?.creditProfileRegistrationId)) return;
      saveReport(state, payload);
    });
  },
});

// Older cached roots and isolated card previews may not include this newly added slice yet.
export const creditScoreCacheSelectors = {
  getSummary: (state: { creditScoreCache?: CreditScoreCacheState }): CachedCreditScore | null =>
    state.creditScoreCache?.summary ?? null,
};

/** Persist only the dashboard's score, date and owning registration; no report details or registration inputs. */
export function stateToCacheCreditScore(state: CreditScoreCacheState): Pick<CreditScoreCacheState, 'summary'> {
  const parsed = summarySchema.safeParse(state.summary);
  return { summary: parsed.success ? parsed.data : null };
}

export function cacheToStateCreditScore(cache: unknown): CreditScoreCacheState {
  const parsed = z.object({ summary: summarySchema.nullable() }).safeParse(cache);
  return { summary: parsed.success ? parsed.data.summary : null, registrationId: null };
}
